import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import { Marquee } from '../components/marquee';
import VideoPlayer from '../components/videoPlayer3';
import { useHideMenu } from '../hooks/useHideMenu';
import { UseSweetAlert } from '../hooks/useSweetAlert';
import { SocketContext } from '../context/SocketContext';
import { getUltimos } from '../helpers/getUltimos';
import { parseVideosLista } from '../helpers/videosMedia';
import { serverApi } from '../api';
import sound from "../assets/new-ticket.mp3";
import { SelectElement } from '../components/selectElement';
import { guardarColoresStorage } from '../helpers/theme';

export const Cola2 = () => {
    useHideMenu(true);

    const [formData, setFormData] = useState({
        codigoEntidad: '',
        codigoUbicacion: ''
    });

    const { socket, conectarSocket } = useContext(SocketContext);

    const [entidadStorage, setEntidadStorage] = useState(
        localStorage.getItem('codigoEntidad') || null
    );
    const [ubicaciones, setUbicaciones] = useState([]);
    const [videoList, setVideoList] = useState(
        localStorage.getItem('videos') || ""
    );

    const [tickets, setTickets] = useState([]);
    const [minutosUltimo, setMinutosUltimo] = useState(0);
    const [currentCalledTicket, setCurrentCalledTicket] = useState(null);
    const [llamadoNuevo, setLlamadoNuevo] = useState(false);
    const [showAudioNotice, setShowAudioNotice] = useState(false);

    // Estado del mensaje marquee (sede → empresa → null)
    const [textoMarquee, setTextoMarquee] = useState(() => {
        const sede = localStorage.getItem('textoMarqueeSede') || '';
        const empresa = localStorage.getItem('textoMarqueeEmpresa') || '';
        return sede.trim() || empresa.trim() || null;
    });

    const getBadgeFontSize = (text, defaultSize = '1.25rem') => {
        if (!text) return defaultSize;
        const len = text.length;
        if (len > 35) return '0.8rem';
        if (len > 25) return '0.92rem';
        if (len > 18) return '1.05rem';
        return defaultSize;
    };

    // ── Audio stack ──────────────────────────────────────────────
    const audioRef = useRef(null);
    const audioCtxRef = useRef(null);
    const ttsUrlRef = useRef(null);

    const getAudio = useCallback(() => {
        if (!audioRef.current) {
            audioRef.current = new Audio(sound);
            audioRef.current.preload = 'auto';
        }
        return audioRef.current;
    }, []);

    const ensureAudioCtx = useCallback(() => {
        if (!audioCtxRef.current) {
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            if (AudioCtx) {
                try { audioCtxRef.current = new AudioCtx(); }
                catch (err) { console.warn("AudioContext creation warning:", err); }
            }
        }
        if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
            audioCtxRef.current.resume().catch(() => {});
        }
        return audioCtxRef.current;
    }, []);

    const unlockAudio = useCallback(() => {
        const audio = getAudio();
        ensureAudioCtx();
        try {
            audio.volume = 0.01;
            const p = audio.play();
            if (p !== undefined) {
                p.then(() => {
                    audio.pause();
                    audio.currentTime = 0;
                    setShowAudioNotice(false);
                }).catch(() => {
                    setShowAudioNotice(true);
                });
            }
        } catch (err) {
            console.warn("Autoplay audio check warning:", err);
        }
    }, [getAudio, ensureAudioCtx]);

    useEffect(() => {
        const handleUserGesture = () => {
            unlockAudio();
            setShowAudioNotice(false);
            window.removeEventListener('click', handleUserGesture);
            window.removeEventListener('touchstart', handleUserGesture);
            window.removeEventListener('keydown', handleUserGesture);
            window.removeEventListener('mousemove', handleUserGesture);
        };
        window.addEventListener('click', handleUserGesture);
        window.addEventListener('touchstart', handleUserGesture);
        window.addEventListener('keydown', handleUserGesture);
        window.addEventListener('mousemove', handleUserGesture);
        unlockAudio();
        return () => {
            window.removeEventListener('click', handleUserGesture);
            window.removeEventListener('touchstart', handleUserGesture);
            window.removeEventListener('keydown', handleUserGesture);
            window.removeEventListener('mousemove', handleUserGesture);
        };
    }, [unlockAudio]);

    useEffect(() => {
        let intentos = 0;
        const maxIntentos = 25;
        const timer = window.setInterval(() => {
            intentos += 1;
            unlockAudio();
            if (intentos >= maxIntentos) window.clearInterval(timer);
        }, 1500);
        return () => window.clearInterval(timer);
    }, [unlockAudio]);

    useEffect(() => {
        return () => {
            if (audioCtxRef.current) {
                audioCtxRef.current.close().catch(() => {});
                audioCtxRef.current = null;
            }
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.src = '';
                audioRef.current = null;
            }
            if (ttsUrlRef.current) {
                URL.revokeObjectURL(ttsUrlRef.current);
                ttsUrlRef.current = null;
            }
        };
    }, []);

    const reproducirSonido = useCallback(() => {
        const audio = getAudio();
        ensureAudioCtx();
        try {
            audio.volume = 1;
            audio.currentTime = 0;
            const playPromise = audio.play();
            if (playPromise !== undefined) {
                playPromise
                    .then(() => setShowAudioNotice(false))
                    .catch((err) => {
                        console.warn("Audio play prevenido por autoplay policy:", err);
                        setShowAudioNotice(true);
                    });
            }
        } catch (e) {
            console.warn("Error ejecutando sonido:", e);
            setShowAudioNotice(true);
        }
    }, [getAudio, ensureAudioCtx]);

    const decirTexto = useCallback((text) => {
        const synth = window.speechSynthesis;
        if (!synth) return;
        const ejecutarVoz = () => {
            try {
                const voices = synth.getVoices();
                const utterThis = new SpeechSynthesisUtterance(text);
                const vocesLatinas = voices.filter(v =>
                    ['es-MX', 'es-CO', 'es-AR', 'es-CL', 'es-PE', 'es-US']
                        .includes(v.lang)
                );
                const vozFemenina = vocesLatinas.find(v =>
                    /paulina|helena|monica|maria|sofia|female/i.test(v.name)
                );
                const vozSeleccionada =
                    vozFemenina ||
                    vocesLatinas[0] ||
                    voices.find(v => v.lang.startsWith('es')) ||
                    voices[0];
                utterThis.voice = vozSeleccionada;
                utterThis.lang = vozSeleccionada?.lang || 'es-CO';
                utterThis.rate = 0.85;
                utterThis.pitch = 1;
                utterThis.onerror = (e) => console.warn("SpeechSynthesis error:", e);
                if (synth.state === 'suspended') synth.resume();
                synth.cancel();
                synth.speak(utterThis);
            } catch (err) {
                console.warn("Error en SpeechSynthesis:", err);
            }
        };
        if (synth.getVoices().length > 0) {
            ejecutarVoz();
        } else {
            synth.onvoiceschanged = ejecutarVoz;
        }
    }, []);

    const hablarLlamado = useCallback(async (mensaje, llamadoVoz, voz) => {
        if (llamadoVoz !== 1) {
            reproducirSonido();
            return;
        }
        try {
            const audio = getAudio();
            ensureAudioCtx();
            const resp = await serverApi.post(
                '/tickets/tts',
                {
                    mensaje,
                    voz: voz || '',
                    codigoEntidad: localStorage.getItem('codigoEntidad') || '',
                },
                { responseType: 'blob' }
            );
            if (!resp || !resp.data || !resp.data.size) {
                throw new Error('Respuesta TTS vacía');
            }
            if (ttsUrlRef.current) {
                URL.revokeObjectURL(ttsUrlRef.current);
            }
            const url = URL.createObjectURL(resp.data);
            ttsUrlRef.current = url;
            audio.src = url;
            audio.volume = 1;
            audio.currentTime = 0;
            const p = audio.play();
            if (p !== undefined) {
                p.then(() => setShowAudioNotice(false))
                    .catch((err) => {
                        console.warn("Audio TTS bloqueado por autoplay:", err);
                        setShowAudioNotice(true);
                        decirTexto(mensaje);
                    });
            } else {
                decirTexto(mensaje);
            }
        } catch (e) {
            const status = e.response?.status;
            if (status === 429) {
                console.warn("TTS rate limit alcanzado (429), usando voz local");
            } else if (!e.response && e.code) {
                console.warn(`TTS: conexión fallida (${e.code}), usando voz local`);
            } else if (status) {
                console.warn(`TTS: error servidor (${status}), usando voz local`);
            } else {
                console.warn("TTS: error desconocido, usando voz local:", e);
            }
            decirTexto(mensaje);
        }
    }, [getAudio, ensureAudioCtx, decirTexto, reproducirSonido]);

    // ── Form handlers ────────────────────────────────────────────
    const handleInputChange = (e) => {
        const newValue = e.target.value;
        if (e.target.id === 'codigoEntidad') {
            setUbicaciones([]);
            setFormData((prev) => ({
                ...prev,
                codigoEntidad: newValue,
                codigoUbicacion: '',
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [e.target.id]: newValue
            }));
        }
    };

    const fetchUbicaciones = useCallback(async (codigo) => {
        if (!codigo || codigo.trim().length < 3) return;
        try {
            const resp = await serverApi.post(
                `/auth/select-ubicacion-pantallas`,
                { codigoEntidad: codigo.trim() }
            );
            if (!resp.data.ubicaciones || resp.data.ubicaciones.length === 0) return;
            const data = resp.data.ubicaciones.map((item) => ({
                value: item.codigoUbicacion,
                label: item.nombreUbicacion
            }));
            setUbicaciones(data);
        } catch (error) {
            console.error("Error al obtener ubicaciones", error);
        }
    }, []);

    useEffect(() => {
        if (formData.codigoEntidad && formData.codigoEntidad.trim().length >= 3) {
            fetchUbicaciones(formData.codigoEntidad.trim());
        }
    }, [formData.codigoEntidad, fetchUbicaciones]);

    const parseVideos = (videosRaw) => parseVideosLista(videosRaw);

    const ingresar = async () => {
        if (formData.codigoUbicacion === "" || formData.codigoEntidad === "")
            return UseSweetAlert("Atención!", 'Falta información', 'error');
        const resp = await serverApi.post(
            `/auth/registro-pantallas`,
            formData
        );
        const pantallas = resp.data.pantallas;
        if (!pantallas || pantallas.length === 0) {
            return UseSweetAlert("Atención!", "Revise la información", "error");
        }
        const pantalla = pantallas[0];
        const listaVideos = parseVideos(pantalla.videos);
        localStorage.setItem('codigoEntidad', formData.codigoEntidad);
        localStorage.setItem('codigoUbicacion', formData.codigoUbicacion);
        localStorage.setItem('llamadoVoz', pantalla.llamadoVoz);
        localStorage.setItem('videos', listaVideos);
        localStorage.setItem('textoMarqueeEmpresa', pantalla.textoMarquee || '');
        localStorage.setItem('textoMarqueeSede', pantalla.textoMarqueeSede || '');

        const marqueeSede = (pantalla.textoMarqueeSede || '').trim();
        const marqueeEmpresa = (pantalla.textoMarquee || '').trim();
        setTextoMarquee(marqueeSede || marqueeEmpresa || null);
        guardarColoresStorage(pantalla.colorPrimario, pantalla.colorSecundario);

        conectarSocket({
            query: {
                codigoEntidad: formData.codigoEntidad,
                ubicacion: formData.codigoUbicacion,
            }
        });
        setEntidadStorage(formData.codigoEntidad);
        setVideoList(listaVideos);
        const tks = await getUltimos();
        setTickets(tks ?? []);
    };

    // ── Cargar videos ────────────────────────────────────────────
    useEffect(() => {
        const fetchVideos = async () => {
            const codigoEntidad = localStorage.getItem("codigoEntidad");
            const codigoUbicacion = localStorage.getItem("codigoUbicacion");
            if (!entidadStorage) return;
            try {
                const resp = await serverApi.post(
                    `/videos/lista-videos-mostrar`,
                    { codigoEntidad, codigoUbicacion }
                );
                if (resp.data.videos && resp.data.videos.length > 0) {
                    const listaVideos = parseVideosLista(resp.data.videos);
                    localStorage.setItem('videos', listaVideos);
                    setVideoList(listaVideos);
                }
            } catch (error) {
                console.error("Error cargando lista de videos:", error);
            }
        };
        fetchVideos();
    }, [entidadStorage]);

    // ── Cargar mensaje marquee cuando el kiosko carga con sesión previa ──
    useEffect(() => {
        const fetchMarquee = async () => {
            const codigoEntidad = localStorage.getItem("codigoEntidad");
            const codigoUbicacion = localStorage.getItem("codigoUbicacion");
            if (!entidadStorage) return;
            try {
                const resp = await serverApi.post(
                    `/auth/registro-pantallas`,
                    { codigoEntidad, codigoUbicacion }
                );
                const pantallas = resp.data.pantallas;
                if (!pantallas || pantallas.length === 0) return;
                const pantalla = pantallas[0];
                const marqueeSede = (pantalla.textoMarqueeSede || '').trim();
                const marqueeEmpresa = (pantalla.textoMarquee || '').trim();
                setTextoMarquee(marqueeSede || marqueeEmpresa || null);
            } catch (error) {
                console.error("Error cargando mensaje marquee:", error);
            }
        };
        fetchMarquee();
    }, [entidadStorage]);

    // ── Socket handlers ──────────────────────────────────────────
    useEffect(() => {
        if (!socket) return;

        const handleAsignados = (ticketsList) => {
            if (!ticketsList || ticketsList.length === 0) return;
            const t = ticketsList[0];

            const hoy = new Date();
            const ultimo = new Date(t.llamadoEn);
            const diferencia = (hoy - ultimo) / (1000 * 60);
            setMinutosUltimo(diferencia);

            setTickets(ticketsList ?? []);
            setCurrentCalledTicket(t);
            setLlamadoNuevo(true);

            const paciente = t.nombrePaciente?.trim() ||
                `Turno ${t.codigoTipoAtencion} ${t.numeroTicket}`;
            const mensaje = `${paciente}, pase a ${t.moduloClase} ${t.modulo}`;

            hablarLlamado(mensaje, t.llamadoVoz, t.voz);

            setTimeout(() => setLlamadoNuevo(false), 9000);
        };

        const handleVolverLlamar = (ticketList) => {
            if (!ticketList || ticketList.length === 0) return;
            const t = ticketList[0];

            const hoy = new Date();
            const ultimo = new Date(t.llamadoEn);
            const diferencia = (hoy - ultimo) / (1000 * 60);
            setMinutosUltimo(diferencia);

            setCurrentCalledTicket(t);
            setLlamadoNuevo(true);

            setTickets(prev => {
                const filtered = prev.filter(item =>
                    !(item.numeroTicket === t.numeroTicket && item.codigoTipoAtencion === t.codigoTipoAtencion)
                );
                return [t, ...filtered];
            });

            const paciente = t.nombrePaciente?.trim() ||
                `Turno ${t.codigoTipoAtencion} ${t.numeroTicket}`;
            const mensaje = `${paciente}, pase a ${t.moduloClase} ${t.modulo}`;

            hablarLlamado(mensaje, t.llamadoVoz, t.voz);

            setTimeout(() => setLlamadoNuevo(false), 9000);
        };

        const handleListar = (ticketsList) => {
            if (!ticketsList || ticketsList.length === 0) {
                setTickets([]);
                return;
            }
            const t = ticketsList[0];
            const hoy = new Date();
            const ultimo = new Date(t.llamadoEn);
            const diferencia = (hoy - ultimo) / (1000 * 60);
            setMinutosUltimo(diferencia);
            setTickets(ticketsList ?? []);
        };

        socket.on('ticket-asignado', handleAsignados);
        socket.on('volver-llamar', handleVolverLlamar);
        socket.on('ticket-listado-13', handleListar);

        return () => {
            socket.off('ticket-asignado', handleAsignados);
            socket.off('volver-llamar', handleVolverLlamar);
            socket.off('ticket-listado-13', handleListar);
        };
    }, [socket, hablarLlamado]);

    // ── Carga inicial ────────────────────────────────────────────
    useEffect(() => {
        const load = async () => {
            const tks = await getUltimos();
            if (!tks || tks.length === 0) return;
            const t = tks[0];
            const hoy = new Date();
            const ultimo = new Date(t.llamadoEn);
            const diferencia = (hoy - ultimo) / (1000 * 60);
            setMinutosUltimo(diferencia);
            setTickets(tks ?? []);
        };
        load();
    }, []);

    // ── Render ───────────────────────────────────────────────────
    return (
        <>
            {entidadStorage !== null ? (
                <div className='px-3 vh-100'>
                    {showAudioNotice && (
                        <div
                            style={{
                                position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)',
                                zIndex: 9999, background: 'rgba(245, 135, 51, 0.92)', color: '#fff',
                                padding: '10px 24px', borderRadius: 30, fontSize: '0.9rem', fontWeight: 700,
                                boxShadow: '0 6px 20px rgba(245, 135, 51, 0.4)', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: 8
                            }}
                            onClick={() => { unlockAudio(); setShowAudioNotice(false); }}
                        >
                            <i className="fa-solid fa-volume-high"></i>
                            <span>Toca en cualquier lugar para habilitar el sonido</span>
                        </div>
                    )}
                    <div className="row" style={{ padding: "2px" }}>
                        <div className="col-6 list-group">
                            <VideoPlayer videos={videoList} />
                        </div>
                        <div className="col-6 list-group" style={{ fontSize: "12px" }}>
                            <table className="table table-borderless">
                                <thead>
                                    <tr>
                                        <th className="text-center fs-2" width="60%">Turno</th>
                                        <th className="text-center fs-2" width="40%">Módulo</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tickets.slice(0, 6).map((ticket, index) => {
                                        const textoModulo = `${ticket.moduloClase} ${ticket.modulo}`;
                                        const esMuyLargo = textoModulo.length > 24;
                                        const esLargo = textoModulo.length > 13;
                                        const fontSizeModulo = esMuyLargo ? 'fs-6' : esLargo ? 'fs-5' : 'fs-4';
                                        const color = (index === 0 && minutosUltimo < 0.10) ? "#f58733" : "#0098da";
                                        return (
                                            <tr key={index} className={index === 0 && minutosUltimo < 0.10 ? 'parpadea' : ''}>
                                                <td width="60%">
                                                    <span className="badge rounded-pill p-1" style={{ width: "100%", height: "50px", backgroundColor: color, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                        {ticket.nombrePaciente.trim() !== ""
                                                            ? <span className='fs-5'>{ticket.codigoTipoAtencion}-{ticket.numeroTicket} {ticket.nombrePaciente}</span>
                                                            : <span className='fs-3'>{ticket.codigoTipoAtencion}-{ticket.numeroTicket} {ticket.numeroDocumento}</span>
                                                        }
                                                    </span>
                                                </td>
                                                <td width="40%">
                                                    <span className={`badge rounded-pill p-1 ${fontSizeModulo}`} style={{ width: "100%", height: "50px", backgroundColor: color, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                        <span style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: "1.1em" }}>
                                                            {textoModulo}
                                                        </span>
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <div className='col-12' style={{ padding: "0px" }}>
                            {textoMarquee && (
                                <div className="App">
                                    <Marquee mensaje={textoMarquee} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="login-container d-flex align-items-center justify-content-center p-3" style={{ minHeight: "100vh", backgroundColor: '#f4f7f6' }}>
                    <div className="col-12 col-md-6 col-lg-4">
                        <div className="card shadow-lg border-0 rounded-4 overflow-hidden" style={{ backgroundColor: '#ffffff' }}>
                            <div className="p-4 text-white text-center" style={{ background: 'linear-gradient(135deg, var(--color-primario) 0%, var(--color-primario-oscuro) 100%)' }}>
                                <div className="d-inline-flex align-items-center justify-content-center mb-2 rounded-circle" style={{ width: 56, height: 56, background: 'rgba(255,255,255,0.15)' }}>
                                    <i className="fa-solid fa-tv fs-3" style={{ color: 'var(--color-secundario)' }}></i>
                                </div>
                                <h4 className="fw-bold m-0" style={{ letterSpacing: '0.5px' }}>Pantalla de Llamado</h4>
                                <p className="mb-0 opacity-75 small">Configuración de Visualización de Turnos</p>
                            </div>
                            <div className="p-4">
                                <div className="mb-3">
                                    <label htmlFor="codigoEntidad" className="form-label small fw-bold text-secondary">Código de Entidad / Empresa</label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light border-end-0">
                                            <i className="fa-solid fa-building" style={{ color: 'var(--color-primario-texto)' }}></i>
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control border-start-0 ps-0 shadow-none"
                                            id="codigoEntidad"
                                            placeholder="Ej: femcc-01"
                                            value={formData.codigoEntidad}
                                            onChange={handleInputChange}
                                            autoComplete="off"
                                        />
                                    </div>
                                </div>
                                {ubicaciones.length > 0 && (
                                    <div className='mb-4'>
                                        <label htmlFor="codigoUbicacion" className="form-label small fw-bold text-secondary">Sede / Ubicación</label>
                                        <SelectElement
                                            options={ubicaciones}
                                            id={"codigoUbicacion"}
                                            name={"codigoUbicacion"}
                                            isMulti={false}
                                            closeOnMenuSelect={true}
                                            className={"shadow-sm rounded-3"}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                )}
                                {formData.codigoUbicacion && (
                                    <button
                                        className="btn w-100 py-3 fw-bold text-white shadow-sm rounded-3 transition-all mt-2"
                                        type="button"
                                        onClick={ingresar}
                                        style={{ background: 'linear-gradient(135deg, var(--color-primario) 0%, var(--color-primario-oscuro) 100%)', border: 'none' }}
                                    >
                                        INGRESAR A LA PANTALLA →
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
